import React, { FC, useEffect, useRef, useState } from "react"

import Button, { BUTTON_SIZES } from "@components/Button/Button"
import { Icon, ICON_TYPES } from "@helpscout/hs-marketing-shared-components"

import "./ReferralForm.scss"

const ReferralForm: FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasInvalidEmail, setHasInvalidEmail] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  const [referralLink, setReferralLink] = useState<string | null>(null)
  const [wasCopied, setWasCopied] = useState<boolean>(false)
  const InputRef = useRef<HTMLInputElement | null>(null)
  const ReferralLinkRef = useRef<HTMLInputElement | null>(null)
  const TimeoutRef = useRef<NodeJS.Timeout | undefined>()

  useEffect(() => {
    if (!wasCopied) return

    const resetWasCopied = () => {
      setWasCopied(false)
    }

    TimeoutRef.current = setTimeout(resetWasCopied, 1000)

    return () => {
      if (TimeoutRef.current) clearTimeout(TimeoutRef.current)
    }
  }, [wasCopied])

  const validateEmail = () => {
    const emailRegex = /\S*?@\S*?\.[a-zA-Z]+/
    const isEmailValid = InputRef.current?.value.match(emailRegex)

    setHasInvalidEmail(!isEmailValid)

    return !!isEmailValid
  }

  const handleSubmit = e => {
    e.preventDefault()

    const isEmailValid = validateEmail()

    if (!isEmailValid) return

    const email = InputRef.current?.value
    setIsLoading(true)

    fetch(`/api/getReferralCode`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({ email }),
    })
      .then(res => res.json())
      .then(json => {
        setIsLoading(false)

        if (!json.success || !json.link) {
          setHasError(true)
          return
        }

        setReferralLink(json.link)
      })
      .catch(() => {
        setIsLoading(false)
        setHasError(true)
      })
  }

  const handleClickCopy = () => {
    navigator.clipboard.writeText(ReferralLinkRef.current?.value || "")
      .then(() => {
        setWasCopied(true)
      })
  }

  return (
    <div className="ReferralForm">
      {!hasError && !isLoading && !referralLink && (
        <>
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="Work Email*"
              ref={InputRef}
            />
            <Button
              onClick={handleSubmit}
              size={BUTTON_SIZES.L}
            >
              Get the Link
            </Button>
          </form>
          {hasInvalidEmail && <p className="error font--text-S">Please enter a valid email address</p>}
        </>
      )}
      {!hasError && isLoading && !referralLink && (
        <span className="loading">
          Getting your unique link...
          <span />
        </span>
      )}
      {!hasError && !isLoading && referralLink && (
        <>
          <div className={`referral-link-wrapper${wasCopied ? " was-copied" : ""}`}>
            <input
              type="text"
              className="referral-link"
              value={wasCopied ? "Copied to clipboard!" : referralLink}
              readOnly
              ref={ReferralLinkRef}
            />
            <button onClick={handleClickCopy} title="Copy to clipboard">
              <Icon type={ICON_TYPES["copy"]} />
            </button>
          </div>
          <p className="welcome font--text-S">Welcome to the program! You can copy this link here and it also has been sent to your inbox.</p>
        </>
      )}
      {hasError && !isLoading && !referralLink && (
        <span className="error">
          Sorry, something went wrong
        </span>
      )}
      <span className="small-print">
        *See <a href="#details">below</a> for details.
      </span>
    </div>
  )
}

export default ReferralForm
